// --------------------------------------------------------------
// Created On: 2025-05-06
// Author: Hannah Vaughan
//
// Last Modified: 2026-07-31
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo } from "react";
import styles from "./AnalogInput.module.scss";
import { ANALOG_INPUTS } from "src/constants/attributes";

// Component that lets the user select an analog input sensor's position, sensor type, and reported attributes.
export default function AnalogInput(props: Props): Component {
  const analogSensorId = useMemo(
    () => getAnalogSensorId(props.sourceAttribute, props.analogSensorMap),
    [props.sourceAttribute, JSON.stringify(props.analogSensorMap)]
  );

  // Returns an analog sensor ID if it exists, otherwise an empty string.
  function getAnalogSensorId(attributeCode: string, analogSensorMap: AnalogSensorMap): number | null {
    if (
      analogSensorMap[attributeCode] !== undefined &&
      analogSensorMap[attributeCode] !== null &&
      analogSensorMap[attributeCode].analogSensorId !== undefined
    ) {
      return analogSensorMap[attributeCode].analogSensorId;
    } else {
      return null;
    }
  }

  // Returns a list of all of the applications supported by a selected analog sensor.
  function getAnalogSensorApplications(attributeCode: string): AnalogApplication[] {
    if (
      props.analogSensorMap[attributeCode] !== undefined &&
      props.analogSensorMap[attributeCode] !== null &&
      props.analogSensorMap[attributeCode].analogSensorId !== undefined
    ) {
      const selectedSensor = props.analogSensors.find(
        (analogSensor) => props.analogSensorMap[attributeCode].analogSensorId === analogSensor.analogSensorId
      );
      if (selectedSensor !== undefined) {
        return selectedSensor.applications;
      }
    }

    return [];
  }

  // Returns an analog sensor's currently selected attribute ID, otherwise an empty string.
  function getSelectedAnalogSensorAttributeId(attributeCode: string): number | null {
    if (
      props.analogSensorMap[attributeCode] !== undefined &&
      props.analogSensorMap[attributeCode] !== null &&
      props.analogSensorMap[attributeCode].mappedAttributeId !== undefined
    ) {
      return props.analogSensorMap[attributeCode].mappedAttributeId;
    } else {
      return null;
    }
  }

  return (
    <div className="row align-items-center mx-1">
      <div className="col g-0">
        <div data-test="analog-sensor-container" className="container mt-2 py-0">
          <div className={`${styles.body} row p-0 m-0`}>
            <div className="col py-2 pe-2">
              <div className="row align-items-center my-2 my-lg-1">
                {/* Dropdown for selecting sensor position. */}
                <div className="col-auto my-2 my-lg-1">
                  <label className={styles.title}>Sensor Position</label>
                  <select
                    data-test="analog-sensor-number-selection"
                    className="form-select"
                    value={props.sensorNumber}
                    onChange={(e) =>
                      props.onChange(
                        props.formId,
                        parseInt(e.target.value, 10),
                        analogSensorId,
                        getSelectedAnalogSensorAttributeId(props.sourceAttribute),
                        ANALOG_INPUTS[parseInt(e.target.value, 10) - 1].attribute
                      )
                    }
                    disabled={props.disabled}
                  >
                    <option value="-1" disabled>
                      Select...
                    </option>
                    {props.sensorPositions.map((sensorPosition) => (
                      <option
                        key={sensorPosition}
                        value={sensorPosition}
                        disabled={
                          (props.analogInputsInUse !== null &&
                            props.analogInputsInUse !== undefined &&
                            Object.keys(props.analogInputsInUse).includes(String(sensorPosition))) ||
                          props.localSensorPositionsInUse.includes(sensorPosition)
                        }
                      >
                        #{sensorPosition}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dropdown for selecting sensor type. */}
                <div className="col my-2 my-lg-1">
                  <label className={styles.title}>Type of Sensor</label>
                  {props.sensorNumber > 0 ? (
                    <select
                      data-test="analog-sensor-type-selection"
                      className="form-select"
                      value={analogSensorId ?? ""}
                      onChange={(e) =>
                        props.onChange(
                          props.formId,
                          props.sensorNumber,
                          parseInt(e.target.value, 10),
                          getSelectedAnalogSensorAttributeId(props.sourceAttribute),
                          props.sourceAttribute
                        )
                      }
                      disabled={props.disabled}
                    >
                      <option value="">Select a sensor type...</option>
                      {props.analogSensors.length > 0 &&
                        props.analogSensors.map((analogSensor) => (
                          <option key={analogSensor.analogSensorId} value={analogSensor.analogSensorId}>
                            {analogSensor.name}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <input type="text" className="form-control" value="Select a sensor type..." disabled />
                  )}
                </div>

                {/* Dropdown for selecting sensor application. */}
                <div className="col my-2 my-lg-1">
                  <label className={styles.title}>Sensor Reports</label>
                  {getAnalogSensorApplications(props.sourceAttribute).length > 0 ? (
                    <select
                      data-test="analog-sensor-detail-selection"
                      className="form-select"
                      value={getSelectedAnalogSensorAttributeId(props.sourceAttribute) ?? ""}
                      onChange={(e) =>
                        props.onChange(
                          props.formId,
                          props.sensorNumber,
                          analogSensorId,
                          parseInt(e.target.value, 10),
                          props.sourceAttribute
                        )
                      }
                      disabled={props.disabled}
                    >
                      <option value="">Select a reporting description...</option>
                      {getAnalogSensorApplications(props.sourceAttribute).map((attribute) => (
                        <option key={attribute.attributeId} value={attribute.attributeId}>
                          {attribute.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" className="form-control" value="Select a reporting description..." disabled />
                  )}
                </div>

                <div className="col-auto">
                  <button
                    data-test="delete-analog-sensor-button"
                    type="button"
                    className={`${styles.button} btn btn-danger float-end`}
                    onClick={() => props.onDelete(props.formId, props.sensorNumber, props.sourceAttribute)}
                    disabled={props.disabled}
                  >
                    <i className="fa fa-fw fa-times fa-xs" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  formIndex: number;
  formId: number;
  sensorNumber: number;
  sourceAttribute: string;
  sensorPositions: number[];
  analogSensors: AnalogSensor[];
  analogSensorMap: AnalogSensorMap;
  analogInputsInUse: SensorInfoMap | null;
  localSensorPositionsInUse: number[];
  disabled: boolean;
  onCreate: () => void;
  onChange: (
    formId: number,
    sensorNumber: number,
    analogSensorId: number | null,
    attributeId: number | null,
    attributeCode: string
  ) => void;
  onDelete: (formId: number, sensorNumber: number, attributeCode: string) => void;
}

interface AnalogSensor {
  analogSensorId: number;
  name: string;
  applications: AnalogApplication[];
}

interface AnalogApplication {
  attributeId: number;
  attributeConnectedId: number;
  name: string;
}

interface AnalogSensorMap {
  [key: string]: AnalogInputSelection;
}

interface AnalogInputSelection {
  analogSensorId: number;
  attributeCode: string;
  attributeConnectedCode: string;
  attributeModeCode: string;
  attributeMinCode: string;
  attributeSpanCode: string;
  mappedAttributeId: number;
  mappedAttributeConnectedId: number;
}

interface SensorInfoMap {
  [sensorNumber: number]: SensorInfo;
}

interface SensorInfo {
  reporting: string;
  assetName: string;
}
